import { useState, useEffect } from 'react';
import { useAuth, apiService } from '../AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, Pencil, Trash2, Save, Search, RefreshCw, MoreVertical,
  Video, Instagram, Play, ExternalLink, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { ImageUploader } from '../../components/ui/ImageUploader';

const videoTypes = [
  { value: 'instagram', label: 'Instagram Reel', icon: Instagram },
  { value: 'youtube', label: 'YouTube Short', icon: Play },
  { value: 'direct', label: 'Direct Video URL', icon: Video },
];

const defaultVideo = {
  id: '', title: '', description: '', thumbnail: '', video_url: '', embed_code: '',
  type: 'instagram', category: '', order: 0, is_active: true, show_on_homepage: true,
  views: 0
};

export const ReelsManager = () => {
  const { token } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [previewModal, setPreviewModal] = useState({ open: false, item: null });

  useEffect(() => { fetchVideos(); }, []);

  const fetchVideos = async () => {
    try {
      const res = await apiService.getVideos(token);
      setVideos(res.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50);

  // Extract Instagram Reel ID from URL
  const extractInstagramId = (url) => {
    const match = url.match(/instagram\.com\/(?:reel|p)\/([A-Za-z0-9_-]+)/);
    return match ? match[1] : null;
  };

  // Extract YouTube video ID from URL
  const extractYoutubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]+)/);
    return match ? match[1] : null;
  };

  const handleSave = async () => {
    const { item } = editModal;
    if (!item.id || !item.title || !item.video_url) {
      toast.error('Please fill in required fields');
      return;
    }

    // Generate embed code based on type
    let embed_code = item.embed_code;
    let thumbnail = item.thumbnail;
    
    if (item.type === 'instagram') {
      const reelId = extractInstagramId(item.video_url);
      if (reelId) {
        embed_code = `<blockquote class="instagram-media" data-instgrm-permalink="https://www.instagram.com/reel/${reelId}/" data-instgrm-version="14"></blockquote>`;
      }
    } else if (item.type === 'youtube') {
      const videoId = extractYoutubeId(item.video_url);
      if (videoId) {
        embed_code = `<iframe src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe>`;
        // Auto-set YouTube thumbnail if not provided
        if (!thumbnail) {
          thumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        }
      }
    }

    const videoData = { ...item, embed_code, thumbnail };

    try {
      if (item.isNew) {
        await apiService.createVideo(token, videoData);
        toast.success('Video added successfully!');
      } else {
        await apiService.updateVideo(token, item.id, videoData);
        toast.success('Video updated successfully!');
      }
      setEditModal({ open: false, item: null });
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving video');
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteVideo(token, deleteDialog.item.id);
      toast.success('Video deleted');
      setDeleteDialog({ open: false, item: null });
      fetchVideos();
    } catch (error) {
      toast.error('Error deleting video');
    }
  };

  const openEditModal = (item = null) => {
    setEditModal({
      open: true,
      item: item ? { ...item, isNew: false } : { ...defaultVideo, id: `video-${Date.now()}`, order: videos.length + 1, isNew: true }
    });
  };

  const filtered = videos.filter(v => {
    const matchSearch = v.title.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || v.type === filterType;
    return matchSearch && matchType;
  });

  const stats = {
    total: videos.length,
    instagram: videos.filter(v => v.type === 'instagram').length,
    youtube: videos.filter(v => v.type === 'youtube').length,
    active: videos.filter(v => v.is_active).length
  };

  return (
    <div className="space-y-6" data-testid="reels-manager">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-['Oswald'] text-2xl font-bold text-slate-900">Reels & Videos</h1>
          <p className="text-slate-500">Showcase your work through Instagram Reels and videos</p>
        </div>
        <Button onClick={() => openEditModal()} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600">
          <Plus className="w-4 h-4 mr-2" /> Add Video
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-sky-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-slate-500">Total Videos</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg flex items-center justify-center">
              <Instagram className="w-5 h-5 text-pink-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.instagram}</p><p className="text-xs text-slate-500">Instagram Reels</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Play className="w-5 h-5 text-red-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.youtube}</p><p className="text-xs text-slate-500">YouTube</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-green-600" />
            </div>
            <div><p className="text-2xl font-bold">{stats.active}</p><p className="text-xs text-slate-500">Active</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search videos..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {videoTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchVideos}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Videos Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 mb-2">No videos found</p>
          <p className="text-sm text-slate-400 mb-4">Add Instagram Reels to showcase your work</p>
          <Button onClick={() => openEditModal()} className="bg-gradient-to-r from-purple-500 to-pink-500">
            <Plus className="w-4 h-4 mr-2" /> Add Your First Video
          </Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.sort((a, b) => a.order - b.order).map((video) => (
            <Card key={video.id} className={`overflow-hidden group ${!video.is_active ? 'opacity-60' : ''}`}>
              <div className="aspect-[9/16] relative bg-slate-900 cursor-pointer" onClick={() => setPreviewModal({ open: true, item: video })}>
                {video.thumbnail ? (
                  <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-pink-900">
                    <Video className="w-16 h-16 text-white/50" />
                  </div>
                )}
                
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                    <Play className="w-8 h-8 text-slate-800 ml-1" />
                  </div>
                </div>

                {/* Type badge */}
                <div className="absolute top-2 left-2">
                  {video.type === 'instagram' && (
                    <span className="px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full flex items-center gap-1">
                      <Instagram className="w-3 h-3" /> Reel
                    </span>
                  )}
                  {video.type === 'youtube' && (
                    <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full flex items-center gap-1">
                      <Play className="w-3 h-3" /> YouTube
                    </span>
                  )}
                </div>

                {/* Menu */}
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="secondary" className="w-8 h-8 opacity-0 group-hover:opacity-100">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => window.open(video.video_url, '_blank')}>
                        <ExternalLink className="w-4 h-4 mr-2" /> Open Original
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditModal(video)}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDeleteDialog({ open: true, item: video })} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Status badges */}
                <div className="absolute bottom-2 left-2 flex gap-1">
                  {!video.is_active && <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded">Hidden</span>}
                  {video.show_on_homepage && <span className="px-2 py-0.5 bg-sky-500 text-white text-xs rounded">Homepage</span>}
                </div>
              </div>
              <CardContent className="p-3">
                <h3 className="font-semibold text-slate-900 text-sm line-clamp-1">{video.title}</h3>
                {video.category && <p className="text-xs text-slate-500">{video.category}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, item: null })}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editModal.item?.isNew ? 'Add New Video' : 'Edit Video'}</DialogTitle>
            <DialogDescription>Add Instagram Reels or YouTube videos to showcase your work</DialogDescription>
          </DialogHeader>
          
          {editModal.item && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Video Type *</Label>
                <Select value={editModal.item.type} onValueChange={(value) => setEditModal({ ...editModal, item: { ...editModal.item, type: value } })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {videoTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <span className="flex items-center gap-2">
                          <t.icon className="w-4 h-4" /> {t.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>
                  {editModal.item.type === 'instagram' ? 'Instagram Reel URL *' : 
                   editModal.item.type === 'youtube' ? 'YouTube Video URL *' : 'Video URL *'}
                </Label>
                <Input
                  value={editModal.item.video_url}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, video_url: e.target.value } })}
                  placeholder={
                    editModal.item.type === 'instagram' ? 'https://www.instagram.com/reel/ABC123...' :
                    editModal.item.type === 'youtube' ? 'https://www.youtube.com/shorts/ABC123...' :
                    'https://example.com/video.mp4'
                  }
                />
                <p className="text-xs text-slate-500">
                  {editModal.item.type === 'instagram' && 'Paste the full Instagram Reel URL'}
                  {editModal.item.type === 'youtube' && 'Paste YouTube video or Shorts URL'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={editModal.item.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    const updates = { title };
                    if (editModal.item.isNew) updates.id = generateSlug(title);
                    setEditModal({ ...editModal, item: { ...editModal.item, ...updates } });
                  }}
                  placeholder="e.g., Office Glass Partition Installation"
                />
              </div>

              <ImageUploader
                label="Thumbnail Image (Optional)"
                value={editModal.item.thumbnail || ''}
                onChange={(url) => setEditModal({ ...editModal, item: { ...editModal.item, thumbnail: url } })}
                aspectRatio={9/16}
                token={token}
              />

              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  value={editModal.item.description || ''}
                  onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, description: e.target.value } })}
                  placeholder="Brief description of the work shown..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input
                    value={editModal.item.category || ''}
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, category: e.target.value } })}
                    placeholder="e.g., Glass Partitions"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={editModal.item.order}
                    onChange={(e) => setEditModal({ ...editModal, item: { ...editModal.item, order: parseInt(e.target.value) || 0 } })}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-6 pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editModal.item.is_active}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, is_active: checked } })}
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editModal.item.show_on_homepage}
                    onCheckedChange={(checked) => setEditModal({ ...editModal, item: { ...editModal.item, show_on_homepage: checked } })}
                  />
                  <Label>Show on Homepage</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, item: null })}>Cancel</Button>
            <Button onClick={handleSave} className="bg-gradient-to-r from-purple-500 to-pink-500">
              <Save className="w-4 h-4 mr-2" /> Save Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={previewModal.open} onOpenChange={(open) => !open && setPreviewModal({ open: false, item: null })}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          {previewModal.item && (
            <div className="bg-black">
              {previewModal.item.type === 'instagram' && (
                <div className="aspect-[9/16] flex items-center justify-center">
                  <iframe
                    src={`https://www.instagram.com/reel/${extractInstagramId(previewModal.item.video_url)}/embed`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}
              {previewModal.item.type === 'youtube' && (
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYoutubeId(previewModal.item.video_url)}`}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}
              {previewModal.item.type === 'direct' && (
                <video src={previewModal.item.video_url} controls className="w-full" />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Video?</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{deleteDialog.item?.title}"?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReelsManager;
